import React from 'react';

import { Box, Chip, Tooltip } from '@mui/material';

import { CommissionsConditionTypes, labelForUserTagValue, ruleConditionsToTextMap } from '~/consts/agentHub';

import type { Commission, CommissionFlightCarrierRule } from '~/types/services/agentHub';

import type { FlightLocationAdditionalInfo } from '~/utils/commissionFlightLocations';

type CommissionRuleValue =
  | string
  | number
  | CommissionFlightCarrierRule
  | { code: string; name?: string }
  | { airlineCode?: string; airlineName?: string };

interface Props {
  commissionGroups?: Commission['commissionGroups'];
  rules?: Record<string, Array<CommissionRuleValue>>;
  /** Flight / bundle extras from API (departure & arrival locations live here, not in `rules`). */
  additionalInfo?: FlightLocationAdditionalInfo;
  agents?: Record<string, string>;
  agencies?: Record<string, string>;
}

type CommissionGroup = NonNullable<Props['commissionGroups']>[number];
type CommissionCondition = CommissionGroup['conditions'][number];
type CommissionConditionOperator = CommissionCondition['operator'];

const CONDITION_OPERATOR_SYMBOL: Record<CommissionConditionOperator, string> = {
  eq: '=',
  neq: '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
};

interface MappedRules {
  agents?: Record<string, string>;
  agencies?: Record<string, string>;
}

function commissionRuleTypeLabel(key: string): string {
  if (key === 'carrier') {
    return ruleConditionsToTextMap.get('flightCarrier') ?? 'Flight carriers';
  }
  if (key === 'flightDeparture') {
    return ruleConditionsToTextMap.get('flightDeparture') ?? 'Departure location';
  }
  if (key === 'flightArrival') {
    return ruleConditionsToTextMap.get('flightArrival') ?? 'Arrival location';
  }
  if (key === 'flightDepartureDate') {
    return ruleConditionsToTextMap.get('flightDepartureDate') ?? 'Flight departure date';
  }
  if (key === 'flightReturnDate') {
    return ruleConditionsToTextMap.get('flightReturnDate') ?? 'Flight return date';
  }
  return ruleConditionsToTextMap.get(key as CommissionsConditionTypes) ?? key;
}

function formatCommissionRuleValue(rule: CommissionRuleValue): string {
  if (typeof rule !== 'object' || rule == null) {
    return String(rule);
  }
  const code = 'code' in rule ? rule.code : rule.airlineCode ?? '';
  const name = 'name' in rule ? rule.name : 'airlineName' in rule ? rule.airlineName : code;
  if (name && code && name !== code) {
    return `${name} (${code})`;
  }
  return code || name || '';
}

function getRuleInfo(key: string, rules: Array<CommissionRuleValue>) {
  switch (key) {
    case 'margin': {
      const [low, high] = rules;
      if (low === undefined || high === undefined) {
        return `${rules.length}`;
      }
      return `${formatCommissionRuleValue(low)}% - ${formatCommissionRuleValue(high)}%`;
    }
    case 'minTotalSpend':
      return rules[0] != null ? String(rules[0]) : '';
    default:
      return `${rules.length}`;
  }
}

function formatAdditionalLocation(loc: FlightLocationAdditionalInfo['departure'] | undefined): string {
  if (!loc) {
    return '';
  }
  return [loc.city, loc.country].filter(Boolean).join(', ');
}

function getRuleText(key: string, rules: Array<CommissionRuleValue>, mappedRules: MappedRules) {
  switch (key) {
    case 'agent': {
      const agentsMap = mappedRules.agents;
      if (!agentsMap) {
        return rules.map(formatCommissionRuleValue).join(' • ');
      }
      return rules
        .map((rule) => (typeof rule === 'string' ? agentsMap[rule] ?? rule : formatCommissionRuleValue(rule)))
        .join(' • ');
    }
    case 'tag': {
      return rules
        .map((rule) => (typeof rule === 'string' ? labelForUserTagValue(rule) : formatCommissionRuleValue(rule)))
        .join(' • ');
    }
    case 'agency': {
      const agenciesMap = mappedRules.agencies;
      if (!agenciesMap) {
        return rules.map(formatCommissionRuleValue).join(' • ');
      }
      return rules
        .map((rule) => (typeof rule === 'string' ? agenciesMap[rule] ?? rule : formatCommissionRuleValue(rule)))
        .join(' • ');
    }
    default:
      return rules.map(formatCommissionRuleValue).join(' • ');
  }
}

function formatCommissionConditionValue(condition: CommissionCondition, mappedRules: MappedRules): string {
  if (condition.type === 'agent') {
    return mappedRules.agents?.[condition.value] ?? condition.value;
  }
  if (condition.type === 'agency') {
    return mappedRules.agencies?.[condition.value] ?? condition.value;
  }
  if (condition.type === 'tag') {
    return labelForUserTagValue(condition.value);
  }
  return condition.value;
}

function getConditionChipTitle(condition: CommissionCondition, mappedRules: MappedRules) {
  return `${commissionRuleTypeLabel(condition.type)} ${
    CONDITION_OPERATOR_SYMBOL[condition.operator]
  } ${formatCommissionConditionValue(condition, mappedRules)}`;
}

function getGroupChipText(group: CommissionGroup, mappedRules: MappedRules) {
  const conditionText = group.conditions
    .map((condition) => getConditionChipTitle(condition, mappedRules))
    .join(` ${group.operator.toLowerCase()} `);

  return `${group.operator}: ${conditionText}`;
}

function AgentHubCommissionGroupRules({
  commissionGroups,
  agents,
  agencies,
}: {
  commissionGroups: Commission['commissionGroups'];
  agents?: Record<string, string>;
  agencies?: Record<string, string>;
}) {
  const mappedRules = { agents, agencies };
  return (
    <Box display="flex" flexWrap="wrap" gap={1}>
      {commissionGroups.map((group) => {
        const groupText = getGroupChipText(group, mappedRules);
        return (
          <Tooltip key={group.id} title={groupText}>
            <Chip sx={{ backgroundColor: '#00000014' }} label={groupText} variant="filled" />
          </Tooltip>
        );
      })}
    </Box>
  );
}

export function AgentHubCommissionRules({ commissionGroups, rules = {}, additionalInfo, agents, agencies }: Props) {
  const hasCommissionGroups = Boolean(commissionGroups?.some((group) => group.conditions.length > 0));
  if (hasCommissionGroups) {
    return <AgentHubCommissionGroupRules commissionGroups={commissionGroups!} agents={agents} agencies={agencies} />;
  }

  const extraChips: Array<{ key: string; label: string; title: string }> = [];
  if (
    !rules.flightDeparture?.length &&
    additionalInfo?.departure &&
    formatAdditionalLocation(additionalInfo.departure)
  ) {
    const text = formatAdditionalLocation(additionalInfo.departure);
    extraChips.push({
      key: 'additionalInfo-departure',
      label: `Departure: ${text}`,
      title: text,
    });
  }
  if (!rules.flightArrival?.length && additionalInfo?.arrival && formatAdditionalLocation(additionalInfo.arrival)) {
    const text = formatAdditionalLocation(additionalInfo.arrival);
    extraChips.push({
      key: 'additionalInfo-arrival',
      label: `Arrival: ${text}`,
      title: text,
    });
  }

  return (
    <Box display="flex" flexWrap="wrap" gap={1}>
      {Object.entries(rules).map(([key, rules]: [string, Array<CommissionRuleValue>]) => (
        <Tooltip key={key} title={getRuleText(key, rules, { agents, agencies })}>
          <Chip
            sx={{ backgroundColor: '#00000014' }}
            label={`${commissionRuleTypeLabel(key)} (${getRuleInfo(key, rules)})`}
            variant="filled"
          />
        </Tooltip>
      ))}
      {extraChips.map((chip) => (
        <Tooltip key={chip.key} title={chip.title}>
          <Chip sx={{ backgroundColor: '#00000014' }} label={chip.label} variant="filled" />
        </Tooltip>
      ))}
    </Box>
  );
}
